import { Button, Modal, Radio, RadioChangeEvent, Space, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { groupNameListCreatedBySystem } from '../../hooks/adminPage/useGroupsAsOptions';
import useSingleCompanySetting from '../../hooks/adminPage/useSingleCompanySetting';
import useCurrentTicket from '../../hooks/ticketDetailPage/useCurrentTicket';
import useFieldAuthorization from '../../hooks/ticketDetailPage/useFieldAuthorization';
import {
    Attributes,
    Group,
    IFieldDefinition,
    TicketSystemFieldsKey,
    settingsKey,
    statusOptions
} from '../../model/types';
import { useCurrentUserStore } from '../../store/RootStoreContext';
import { isAdmin, isAdminOrAgent, isAgent, isEndUser } from '../../utils/accountHelper';
import {
    parseSerializedValueFromUserFormValue,
    parseUserIdFromSerializedValue
} from '../../utils/forms/formHelper.parser';
import { defineMode } from '../../utils/forms/formHelpers';
import { nullishValue } from '../../utils/helpers';
import { isPublicPage } from '../../utils/routeHelper';
import getStore from '../../utils/store/getStore';
import { getTicketStatus } from '../../utils/ticketHelper';
import { GrispiInputContainerProps } from '../GrispiInput/GrispiInputContainer';
import GrispiInput from '../GrispiInput/index';
import CallNowActionButton from '../plugins/CallNowActionButton';

export interface CustomFieldProps {
    antdProps?: Record<string, any> & { ref?: any }; //TODO: Bünyamin => make compatible type any
    checkboxDefaultValue?: null | string;
    containerProps?: GrispiInputContainerProps;
    disabled?: boolean;
    fieldDefinition: IFieldDefinition;
    noLabel?: boolean;
    onBlur?: () => void; // easy reference for usage with any form
    onChange: (value: any) => void;
    userValidationProp?: {
        onError: (message: string) => void;
        onValid: () => void;
    };
    value?: any;
}

const defaultActionButtonText = {
    [TicketSystemFieldsKey.assignee]: 'assignToMe.buttonName',
    [TicketSystemFieldsKey.followers]: 'follow'
};

const getCurrentUserAssigneeSerializedValue = (userId: number, groups: Group[]) => {
    if (!groups?.length) {
        return `${null}:${userId}`;
    }
    if (groups?.length === 1) {
        return `${groups[0]?.id}:${userId}`;
    }
    if (groups?.length > 1) {
        return groups;
    }
};

const ByMeActionButton = ({ disabled = false, onClick }: { disabled?: boolean; onClick: () => void }) => {
    const { t } = useTranslation('forms');
    return (
        <Button
            disabled={disabled}
            type="link"
            size="small"
            style={{ fontSize: 11, paddingRight: 0 }}
            onClick={onClick}
        >
            {t('by me')}
        </Button>
    );
};
const AssignToMeActionButton = ({
    disabled = false,
    onClick
}: {
    disabled?: boolean;
    onClick: (defaultUSerGroup: string) => void;
}) => {
    const { t } = useTranslation('forms');
    const { data, isLoading } = useSingleCompanySetting(settingsKey.DEFAULT_USER_GROUP);
    return (
        <Button
            disabled={disabled || isLoading}
            type="link"
            size="small"
            style={{ fontSize: 11, paddingRight: 0 }}
            onClick={() => onClick(data?.value)}
        >
            {t(defaultActionButtonText[TicketSystemFieldsKey.assignee])}
        </Button>
    );
};

const CustomField = ({
    antdProps = {},
    containerProps = {},
    disabled,
    fieldDefinition,
    onChange,
    value,
    ...rest
}: CustomFieldProps) => {
    const { t } = useTranslation('forms');
    const { asPath, pathname } = useRouter();
    const [modal, contextHolder] = Modal.useModal();
    const isPublic = isPublicPage(pathname);
    const { email, id: currentUserId, phone, fullName, role, groups } = useCurrentUserStore() ?? {};
    const { isAuthorizationRequired } = useFieldAuthorization(!isPublic ? fieldDefinition.key : null);
    const isDisabledByAuthorization = isAuthorizationRequired && !isAdmin(role);

    const currentSerializedUser = email
        ? `${[currentUserId || null, email, phone || null].join(':')}|${fullName} (${email})`
        : null;
    const currentFriendlyUser = email ? `${currentUserId}|${fullName} (${email})` : null;

    const { data: ticketDetail } = useCurrentTicket();
    const isClosedTicket = getTicketStatus(ticketDetail) === statusOptions.Closed;

    const handleOnChange = useCallback(
        (v) => {
            const controlledValue = Array.isArray(v) && v.length === 0 ? undefined : v;
            onChange(controlledValue || null);

            getStore().MacroStore.setPlaceholderValue(
                fieldDefinition.key as any,
                fieldDefinition.type,
                controlledValue
            );
            // onBlur();
        },
        [fieldDefinition.key, fieldDefinition.type, onChange]
    );
    const handleAssignToMe = useCallback(
        (defaultUSerGroup: string) => {
            const assigneeGroups = getCurrentUserAssigneeSerializedValue(currentUserId, groups);
            if (typeof assigneeGroups === 'string') handleOnChange(assigneeGroups);
            else {
                const currentGroup = assigneeGroups?.find((group) => group.id === +value?.split(':')[0]);
                let selectedGroupId = +currentGroup?.id || +defaultUSerGroup;
                const onRadioChange = (e: RadioChangeEvent) => {
                    selectedGroupId = e.target.value;
                };
                modal.info({
                    title: t('assignToMe.title'),
                    centered: true,
                    content: (
                        <div>
                            <p>{t('assignToMe.description', { count: assigneeGroups?.length })}</p>
                            <Radio.Group onChange={onRadioChange} defaultValue={selectedGroupId}>
                                <Space direction="vertical">
                                    {assigneeGroups?.map(({ name, id }) => (
                                        <Radio key={id} value={id}>
                                            {groupNameListCreatedBySystem.includes(name)
                                                ? t(`form.GroupNames.${name}` as any)
                                                : name}
                                            {id === +defaultUSerGroup && (
                                                <Tag style={{ marginLeft: '0.5em' }}>{t('assignToMe.default')}</Tag>
                                            )}
                                        </Radio>
                                    ))}
                                </Space>
                            </Radio.Group>
                        </div>
                    ),
                    onOk() {
                        handleOnChange(`${selectedGroupId}:${currentUserId}`);
                    }
                });
            }
        },
        [currentUserId, groups, handleOnChange, modal, t, value]
    );
    if (nullishValue(fieldDefinition)) return null;

    const mode = defineMode(asPath);
    const isCreateMode = mode === 'create';

    const renderAssignToMeActionButton = fieldDefinition.key === TicketSystemFieldsKey.assignee;
    const renderFollowActionButton = fieldDefinition.key === TicketSystemFieldsKey.followers;
    const renderByMeActionButton =
        fieldDefinition.key === TicketSystemFieldsKey.requester && !value?.includes(currentSerializedUser);

    const renderCallNowAction =
        fieldDefinition.key === TicketSystemFieldsKey.requester &&
        !isCreateMode &&
        value &&
        !value?.includes(currentSerializedUser);

    const isActionButtonDisabled =
        isClosedTicket ||
        isDisabledByAuthorization ||
        (typeof value === 'string' && value?.includes(currentSerializedUser)) ||
        (typeof value === 'string' && value?.includes(currentFriendlyUser));

    const defaultActionButton = renderFollowActionButton ? (
        <Button
            disabled={isActionButtonDisabled}
            type="link"
            size="small"
            style={{ fontSize: 11, paddingRight: 0 }}
            onClick={() => handleOnChange(value ? `${value},${currentFriendlyUser}` : currentFriendlyUser)}
        >
            {t(defaultActionButtonText[fieldDefinition.key])}
        </Button>
    ) : renderAssignToMeActionButton ? (
        <>
            <AssignToMeActionButton onClick={handleAssignToMe} disabled={isActionButtonDisabled} />
            {contextHolder}
        </>
    ) : renderCallNowAction ? (
        <CallNowActionButton
            userId={parseUserIdFromSerializedValue(parseSerializedValueFromUserFormValue(value))?.toString()}
        />
    ) : renderByMeActionButton ? (
        <ByMeActionButton disabled={isActionButtonDisabled} onClick={() => handleOnChange(currentSerializedUser)} />
    ) : null;

    const label =
        (isEndUser(role) || typeof role === 'undefined'
            ? fieldDefinition.titleForEndUsers
            : isAdminOrAgent(role)
              ? fieldDefinition.titleForAgents
              : fieldDefinition.name) || fieldDefinition.name;

    const description = !isAdminOrAgent(role)
        ? fieldDefinition.descriptionForEndUsers
        : isAgent(role)
          ? fieldDefinition.descriptionForAgents
          : null;

    return (
        <GrispiInput
            antdProps={antdProps}
            containerProps={{
                actionButton: containerProps?.actionButton || defaultActionButton,
                description,
                label,
                // isMandatory: isAdminOrAgent(role) ? containerProps.isMandatory : 'NOT_REQUIRED',
                ...containerProps
            }}
            disabled={disabled || isDisabledByAuthorization}
            isNewValueAllowed={fieldDefinition.attributes.includes(Attributes.ALLOW_NEW_VALUES)}
            // onBlur={onBlur}
            onChange={handleOnChange}
            options={fieldDefinition.options}
            type={fieldDefinition.type}
            value={value}
            fieldKey={fieldDefinition.key}
            {...(fieldDefinition.key === TicketSystemFieldsKey.followers ? { isAgentInput: true } : {})}
            {...rest}
        />
    );
};
export default observer(CustomField);
